import React, { useEffect, useRef, useState } from 'react';
import { db } from '../services/db';
import { Project } from '../models/types';
import { compressImageToJpeg } from '../utils/imageCompression';
import { enqueueReceipt, syncPendingReceipts } from '../services/receiptQueue';
import '../styles/Modal.css';
import '../styles/ReceiptCapture.css';

interface ReceiptCaptureProps {
  onClose: () => void;
  onQueued?: () => void;
}

// Must stay byte-identical to RECEIPT_CATEGORIES in the dashboard's
// src/lib/receipts.ts — the ingest endpoint validates against that list and
// rejects anything else. Two repos, no shared package, so this is a manually
// synced duplicate.
const RECEIPT_CATEGORIES = [
  'Materials',
  'Shop supplies',
  'Tools & equipment',
  'Fuel & vehicle',
  'Meals',
  'Subcontractors',
  'Office & supplies',
  'Software & subscriptions',
  'Advertising & marketing',
  'Shop rent & utilities',
  'Other expense',
];

const PAYMENT_METHODS: Array<{ value: string; label: string }> = [
  { value: 'biz_cb', label: 'BizCB' },
  { value: 'biz_pb', label: 'BizPB' },
  { value: 'biz_amex', label: 'BizAMEX' },
  { value: 'personal_reimburse', label: 'Personal-Reimburse' },
  { value: 'cash', label: 'Cash' },
];

function todayLocalISODate(): string {
  const now = new Date();
  const tzOffsetMs = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - tzOffsetMs).toISOString().slice(0, 10);
}

const ReceiptCapture: React.FC<ReceiptCaptureProps> = ({ onClose, onQueued }) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [project, setProject] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [vendor, setVendor] = useState('');
  const [amount, setAmount] = useState('');
  const [txnDate, setTxnDate] = useState(todayLocalISODate());
  const [category, setCategory] = useState(RECEIPT_CATEGORIES[0]);
  const [paymentMethod, setPaymentMethod] = useState(() => {
    // Distrust the stored value: it's only ever valid if it's still one of
    // today's methods. A stale bundle (PWA rollback) or the deferred
    // Check/Charge methods landing later could leave a value here that no
    // longer matches any <option>, which would show "BizCB" in the UI while
    // state silently holds the unmatched string — and that string still gets
    // sent, 400ing at the endpoint and stranding the receipt.
    const stored = localStorage.getItem('receipts:lastPaymentMethod');
    return stored && PAYMENT_METHODS.some((m) => m.value === stored) ? stored : PAYMENT_METHODS[0].value;
  });
  const [billable, setBillable] = useState(true);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState<{ text: string; kind: 'success' | 'error' } | null>(null);

  const cameraInputRef = useRef<HTMLInputElement>(null);
  const libraryInputRef = useRef<HTMLInputElement>(null);
  // Mirrors previewUrl so the unmount cleanup below can revoke whatever the
  // latest URL was without depending on previewUrl (which would re-run this
  // effect's cleanup — and revoke the URL — on every replace, not just on
  // unmount).
  const previewUrlRef = useRef<string | null>(null);
  useEffect(() => {
    previewUrlRef.current = previewUrl;
  }, [previewUrl]);

  useEffect(() => {
    // previewUrl is revoked on replace (handleFileChange) and on reset
    // (resetForNextCapture), but neither runs if the employee stages a photo
    // and then taps Close: nothing else would ever revoke that blob URL, and
    // it — plus the compressed JPEG behind it — would leak for the life of
    // the page. Runs once, on true unmount, via the ref above.
    return () => {
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    };
  }, []);

  useEffect(() => {
    // Guards against writing state (or reading localStorage) after this
    // instance has unmounted: without it, a still-in-flight IndexedDB read
    // that resolves post-unmount throws trying to touch a torn-down
    // environment instead of quietly doing nothing.
    let cancelled = false;
    loadProjects(() => cancelled);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Default the job to whatever the employee is clocked into right now, because
   * a receipt is almost always for the job they are standing on. Falls back to
   * the last job they filed a receipt against, which is what the dashboard's
   * capture page does.
   */
  const loadProjects = async (isCancelled: () => boolean) => {
    try {
      const allProjects = await db.projects.toArray();
      if (isCancelled()) return;
      setProjects(allProjects);

      const openEntry = (await db.timeEntries.toArray())
        .filter((e) => !e.timeOut && !e.deletedAt && e.project)
        .sort((a, b) => new Date(b.timeIn).getTime() - new Date(a.timeIn).getTime())[0];
      if (isCancelled()) return;

      const lastProject = localStorage.getItem('receipts:lastProject');
      const preferred = [openEntry?.project, lastProject].find(
        (name) => name && allProjects.some((p) => p.name === name)
      );

      if (preferred) setProject(preferred);
    } catch (error) {
      if (!isCancelled()) console.error('Error loading projects:', error);
    }
  };

  const handleAddProject = async () => {
    const entered = window.prompt('Enter new job name:');
    if (!entered || !entered.trim()) return;
    const projectName = entered.trim();

    try {
      const existing = await db.projects.where('name').equals(projectName).toArray();
      if (existing.length === 0) {
        // No remoteId: the dashboard has not seen this job. The receipt carries
        // the name and the dashboard resolves or creates it, the same way a
        // phone-typed job reaches the office through time-entry sync.
        await db.projects.add({ name: projectName, tasks: [] });
      }
      setProjects(await db.projects.toArray());
      setProject(projectName);
    } catch (error) {
      console.error('Error adding project:', error);
      window.alert('Could not add that job. Please try again.');
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const compressed = await compressImageToJpeg(file);
    setImageFile(compressed);
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(compressed);
    });
  };

  const resetForNextCapture = () => {
    setImageFile(null);
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setVendor('');
    setAmount('');
    setTxnDate(todayLocalISODate());
    setNotes('');
    if (cameraInputRef.current) cameraInputRef.current.value = '';
    if (libraryInputRef.current) libraryInputRef.current.value = '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Defense-in-depth only: the real double-submit guard is `disabled={saving}`
    // on the Save button below, which stops a second real click from ever
    // reaching this handler. This check reads the `saving` *state* (not a
    // ref), so it cannot catch two dispatches fired within the same tick
    // before React re-renders — that's not a real double-tap path (a second
    // physical tap requires an event-loop turn the disabled attribute has
    // already had by then), so this line is a cheap belt-and-suspenders
    // check, not the mechanism actually preventing enqueueReceipt from
    // minting two distinct receipts (it generates a fresh clientUuid every
    // call, so server-side dedup on clientUuid can't collapse a double-tap
    // that got through).
    if (saving) return;

    setStatusMessage(null);

    if (!imageFile) {
      setStatusMessage({ text: 'Take or choose a photo first', kind: 'error' });
      return;
    }
    if (!vendor.trim()) {
      setStatusMessage({ text: 'Vendor is required', kind: 'error' });
      return;
    }
    const amountNum = Number(amount);
    if (!Number.isFinite(amountNum) || amountNum <= 0) {
      setStatusMessage({ text: 'Enter a valid amount', kind: 'error' });
      return;
    }

    setSaving(true);
    try {
      const selected = projects.find((p) => p.name === project);

      await enqueueReceipt({
        image: imageFile,
        vendor: vendor.trim(),
        // Cents from a decimal string without float drift: round to the
        // nearest cent from the number of cents itself (amountNum * 100),
        // not from repeated float arithmetic on dollars. "42.50" -> 4250,
        // exactly, and even inputs like "0.1" + "0.2"-style edge cases only
        // ever go through one multiply-and-round, so there is nowhere for
        // drift to accumulate.
        amountCents: Math.round(amountNum * 100),
        txnDate,
        category,
        paymentMethod,
        billable,
        notes: notes.trim() || undefined,
        // Always send the name alongside the id when both are known (review
        // finding: the id-then-name fallback the dashboard runs is dead code
        // unless the name travels too — see receiptQueue.ts's buildForm).
        ...(selected?.remoteId != null ? { jobRemoteId: selected.remoteId } : {}),
        ...(selected ? { jobName: selected.name } : {}),
      });

      localStorage.setItem('receipts:lastProject', project);
      localStorage.setItem('receipts:lastPaymentMethod', paymentMethod);

      // The receipt is safe in IndexedDB now, so this succeeds regardless of
      // signal. Delivery is attempted but deliberately not awaited for the
      // success message — a lumberyard with no bars must still say "Saved".
      setStatusMessage({ text: 'Saved ✓ — Add another', kind: 'success' });
      resetForNextCapture();
      onQueued?.();

      syncPendingReceipts().catch(() => {
        /* the queue retries; nothing to show the employee here */
      });
    } catch (error) {
      console.error('Error queueing receipt:', error);
      setStatusMessage({ text: 'Could not save the receipt on this device. Please try again.', kind: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2>New Receipt</h2>
        <p className="receipt-subtitle">Photo → a few fields → save. About 20 seconds.</p>

        {statusMessage && (
          <div className={`receipt-status receipt-status-${statusMessage.kind}`}>{statusMessage.text}</div>
        )}

        <form onSubmit={handleSubmit}>
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleFileChange}
            className="receipt-file-input"
            id="receipt-photo-camera"
          />
          <input
            ref={libraryInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="receipt-file-input"
            id="receipt-photo-library"
          />

          {previewUrl ? (
            <div className="receipt-preview">
              <img src={previewUrl} alt="Receipt preview" />
              <div className="receipt-preview-actions">
                <button type="button" onClick={() => cameraInputRef.current?.click()}>
                  Retake photo
                </button>
                <button type="button" onClick={() => libraryInputRef.current?.click()}>
                  Choose from library
                </button>
              </div>
            </div>
          ) : (
            <div className="receipt-photo-picker">
              <label htmlFor="receipt-photo-camera" className="receipt-photo-option">
                Take photo
              </label>
              <label htmlFor="receipt-photo-library" className="receipt-photo-option">
                Choose photo
              </label>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="vendor">Vendor:</label>
            <input id="vendor" value={vendor} onChange={(e) => setVendor(e.target.value)} placeholder="Home Depot" required />
          </div>

          <div className="receipt-row">
            <div className="form-group">
              <label htmlFor="amount">Amount:</label>
              <input
                id="amount"
                type="number"
                step="0.01"
                min="0.01"
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="42.50"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="txnDate">Date:</label>
              <input id="txnDate" type="date" value={txnDate} onChange={(e) => setTxnDate(e.target.value)} required />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="project">Job:</label>
            <div className="select-with-button">
              <select id="project" value={project} onChange={(e) => setProject(e.target.value)}>
                <option value="">No job (shop/overhead)</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.name}>
                    {p.name}
                  </option>
                ))}
              </select>
              <button type="button" onClick={handleAddProject} className="add-button">
                +
              </button>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="category">Category:</label>
            <select id="category" value={category} onChange={(e) => setCategory(e.target.value)}>
              {RECEIPT_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="paymentMethod">Paid with:</label>
            <select id="paymentMethod" value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
              {PAYMENT_METHODS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group receipt-billable">
            <label htmlFor="billable">
              <input id="billable" type="checkbox" checked={billable} onChange={(e) => setBillable(e.target.checked)} />
              Billable to job
            </label>
          </div>

          <div className="form-group">
            <label htmlFor="notes">Notes (optional):</label>
            <input id="notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="What it's for" />
          </div>

          <div className="modal-buttons-vertical">
            <button type="submit" className="save-btn" disabled={saving}>
              {saving ? 'Saving…' : 'Save receipt'}
            </button>
            <button type="button" className="cancel-btn" onClick={onClose}>
              Close
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReceiptCapture;
